import React, { FC, memo, useCallback, useEffect, useState } from 'react'
import { Grid, List, ListItem } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { toast } from 'react-toastify'
import { FormProvider, useForm } from 'react-hook-form'
import { Modal } from '../../../../../components/Dashboard/Modal'
import { resolver } from './resolver'
import { useAuth, useRest } from '../../../../../components/Context'
import { InputCodeField } from '../../../../../components/Dashboard/InputCodeField'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginBottom: theme.spacing(-3),
    width: 270,
    fontSize: '14px',
    '& li span': {
      color: 'black',
      fontWeight: 'bold',
      textAlign: 'center',
      lineHeight: 1.5,
      background: theme.palette.secondary.light,
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      marginRight: '10px',
      fontSize: '14px',
    },
    '& li:nth-child(2)': {
      flexWrap: 'wrap',
    },
  },
  digits: {
    width: 56,
    margin: 0,
    padding: theme.spacing(6, 0, 1),
    [theme.breakpoints.down('xs')]: {
      width: 40,
      padding: theme.spacing(4, 0, 1),
    },
    '& .MuiInputBase-root:hover:before': {
      borderWidth: 1,
    },
    '& .MuiInputBase-root.Mui-focused:after': {
      borderWidth: 1,
    },
    '& .MuiInputBase-root.Mui-disabled': {
      backgroundColor: '#f5f5f5',
      '&:before': {
        borderBottomStyle: 'solid',
      },
    },
    '& .MuiInputBase-input': {
      height: 80,
      padding: theme.spacing(2, 0),
      fontSize: '2.25rem',
      lineHeight: 1.13,
      textAlign: 'center',
      boxSizing: 'border-box',
      [theme.breakpoints.down('xs')]: {
        height: 56,
        fontSize: '1.5rem',
      },
    },
  },
  code: {
    display: 'flex',
    justifyContent: 'center',
    margin: theme.spacing(3, 0, 1, 0),
  },
  multiInput: {
    margin: '0 auto',
  },
}))

interface ITwoFactorAuthenticationModal {
  isOpen: boolean
  modalClose: () => void
  modalTwoFa: (value: boolean) => void
}

type Inputs = {
  twoFaCode: string
}

const TwoFactorAuthenticationModal: FC<ITwoFactorAuthenticationModal> = ({ isOpen, modalClose, modalTwoFa }) => {
  const formMethods = useForm<Inputs>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
    resolver,
  })
  const { handleSubmit } = formMethods

  const classes = useStyles()
  const { saveToken } = useAuth()
  const { rxiosAuth, rest } = useRest()

  const [qrCode, setQrCode] = useState<string>()

  const blobToBase64 = useCallback((blob: Blob) => {
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.readAsDataURL(blob)
      reader.onerror = (error) => {
        toast.error(error)
      }
    })
  }, [])

  const twoFactorGenerateQrCode = useCallback(async () => {
    try {
      const { data } = await rest.post(
        '2fa/generate',
        {},
        {
          responseType: 'blob',
        },
      )
      const Base64 = (await blobToBase64(data)) || ''
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setQrCode(Base64)
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      toast.error(err.message)
    }
    // eslint-disable-next-line
  }, [])

  const onSubmit = useCallback(
    async (data: Inputs) => {
      rxiosAuth.post('2fa/turn-on', data).subscribe(
        // eslint-disable-next-line
        (next: any) => {
          const response = { ...next }
          if (response.status === 'success') {
            saveToken(next.access_token)
            modalTwoFa(true)
            formMethods.reset()
            toast.success(next.message)
          }
        },
        (error) => {
          toast.error(error.response.data.message)
        },
      )
    },
    [modalTwoFa, rxiosAuth, formMethods, saveToken],
  )

  const getPressEnterEvent = useCallback(
    (isEnterPressed: boolean, data: Inputs) => {
      if (isEnterPressed) onSubmit(data)
    },
    [onSubmit],
  )

  useEffect(() => {
    if (isOpen) twoFactorGenerateQrCode()
  }, [isOpen, twoFactorGenerateQrCode])

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        formMethods.reset()
      }, 300)
    }
  }, [formMethods, isOpen])

  return (
    <Modal
      title="Set Up Two-Factor Authentication"
      isOpen={isOpen}
      modalClose={modalClose}
      onSubmit={handleSubmit(onSubmit)}
      submitTitle={'Verify'}
      isSubmit
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container justifyContent="center">
            <Grid item className={classes.root}>
              <List>
                <ListItem>
                  <span>1</span>Download Google Authenticator
                </ListItem>
                <ListItem>
                  <span>2</span>Use it to scan the QR code
                  <Grid item xs={12} className={classes.code}>
                    <img src={qrCode} alt="Qr Code" />
                  </Grid>
                </ListItem>
                <ListItem>
                  <span>3</span>Enter the Authentication Code
                </ListItem>
              </List>

              <Grid item xs={11} className={classes.multiInput}>
                <InputCodeField width={35} height={35} getPressEnterEvent={getPressEnterEvent} />
              </Grid>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </Modal>
  )
}

export const MemoTwoFactorAuthenticationModal = memo(TwoFactorAuthenticationModal)
